import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EMPTY_STAT = { value: "", label: "" };

export default function AboutAdmin() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/about").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  if (!form) {
    return <div className="font-body text-sm text-neutral-500" data-testid="about-admin-loading">Loading…</div>;
  }

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onStatChange = (idx, k) => (e) => {
    setForm((f) => {
      const stats = [...(f.stats || [])];
      stats[idx] = { ...stats[idx], [k]: e.target.value };
      return { ...f, stats };
    });
  };

  const addStat = () => setForm((f) => ({ ...f, stats: [...(f.stats || []), EMPTY_STAT] }));
  const removeStat = (idx) =>
    setForm((f) => ({ ...f, stats: (f.stats || []).filter((_, i) => i !== idx) }));

  const onPortraitUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image larger than 10MB.");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const r = await api.post("/uploads", data, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, portrait_url: `${BACKEND}${r.data.url}` }));
      toast.success("Portrait uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/about", {
        portrait_url: form.portrait_url,
        overline: form.overline,
        headline: form.headline,
        background_label: form.background_label,
        background_text: form.background_text,
        approach_label: form.approach_label,
        approach_text: form.approach_text,
        stats: (form.stats || []).filter((s) => s.value || s.label),
      });
      toast.success("About section saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const portraitSrc = form.portrait_url?.startsWith("/") ? `${BACKEND}${form.portrait_url}` : form.portrait_url;

  return (
    <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="about-admin">
      <section className="lg:col-span-7 bg-white border border-neutral-200 p-6 space-y-1">
        <div>
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Overline (small label above headline)</label>
          <input value={form.overline || ""} onChange={onField("overline")} className="input-bare" data-testid="about-overline-input" />
        </div>

        <div className="pt-4">
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Headline</label>
          <textarea
            value={form.headline || ""}
            onChange={onField("headline")}
            rows={3}
            className="input-bare resize-none"
            data-testid="about-headline-input"
          />
          <p className="font-body text-[11px] text-neutral-500 pt-1">Wrap a word in *asterisks* to render it as italic accent (e.g. *type*).</p>
        </div>

        <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          <div>
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Block 1 — Label</label>
            <input value={form.background_label || ""} onChange={onField("background_label")} className="input-bare" data-testid="about-bg-label-input" />
          </div>
          <div>
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Block 2 — Label</label>
            <input value={form.approach_label || ""} onChange={onField("approach_label")} className="input-bare" data-testid="about-ap-label-input" />
          </div>
          <div>
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 pt-4 block">Block 1 — Text</label>
            <textarea value={form.background_text || ""} onChange={onField("background_text")} rows={5} className="input-bare resize-none" data-testid="about-bg-text-input" />
          </div>
          <div>
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 pt-4 block">Block 2 — Text</label>
            <textarea value={form.approach_text || ""} onChange={onField("approach_text")} rows={5} className="input-bare resize-none" data-testid="about-ap-text-input" />
          </div>
        </div>

        <div className="pt-8">
          <div className="flex items-center justify-between mb-3">
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Stats</label>
            <button type="button" onClick={addStat} className="font-body text-xs uppercase tracking-[0.2em] link-line" data-testid="about-add-stat-button">
              + Add stat
            </button>
          </div>
          <div className="space-y-2">
            {(form.stats || []).map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 items-center" data-testid={`about-stat-row-${i}`}>
                <input
                  value={s.value}
                  onChange={onStatChange(i, "value")}
                  placeholder="Value (120+)"
                  className="col-span-4 border border-neutral-200 px-3 py-2 font-display font-bold text-xl"
                  data-testid={`about-stat-value-${i}`}
                />
                <input
                  value={s.label}
                  onChange={onStatChange(i, "label")}
                  placeholder="Label (Projects)"
                  className="col-span-6 border border-neutral-200 px-3 py-2 font-body text-sm"
                  data-testid={`about-stat-label-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeStat(i)}
                  className="col-span-2 font-body text-xs uppercase tracking-[0.2em] text-[#FF3E00] link-line"
                  data-testid={`about-stat-remove-${i}`}
                >
                  Remove
                </button>
              </div>
            ))}
            {(!form.stats || form.stats.length === 0) && (
              <p className="font-body text-sm text-neutral-500">No stats yet.</p>
            )}
          </div>
        </div>
      </section>

      <aside className="lg:col-span-5 space-y-6">
        <div className="bg-white border border-neutral-200 p-6">
          <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-4">Portrait</p>
          {portraitSrc && (
            <img src={portraitSrc} alt="portrait" className="w-full h-64 object-cover mb-4 border border-neutral-100" data-testid="about-portrait-preview" />
          )}
          <input
            value={form.portrait_url || ""}
            onChange={onField("portrait_url")}
            placeholder="Portrait URL or upload below"
            className="input-bare"
            data-testid="about-portrait-url-input"
          />
          <label className="inline-flex items-center gap-2 mt-4 px-4 py-2 border border-neutral-300 font-body text-xs uppercase tracking-[0.2em] cursor-pointer hover:bg-neutral-50" data-testid="about-upload-portrait-label">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onPortraitUpload}
              className="hidden"
              disabled={uploading}
              data-testid="about-upload-portrait-input"
            />
            {uploading ? "Uploading…" : "↑ Upload portrait"}
          </label>
          {form.portrait_url && (
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, portrait_url: "" }))}
              className="ml-3 font-body text-xs uppercase tracking-[0.2em] text-[#FF3E00] link-line"
              data-testid="about-clear-portrait-button"
            >
              Clear
            </button>
          )}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-[#0A0A0A] text-[#F4F4F0] py-4 font-display font-bold uppercase tracking-widest text-sm hover:bg-[#FF3E00] transition-colors disabled:opacity-40"
          data-testid="about-save-button"
        >
          {saving ? "Saving…" : "Save About Section"}
        </button>

        <p className="font-body text-[11px] text-neutral-500 leading-relaxed">
          Changes appear immediately on the public homepage at the &quot;About&quot; section.
        </p>
      </aside>
    </form>
  );
}
